import { useMutation } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { DeleteCompanyResponseSchema } from "../response/DeleteCompanyResponse";

/**
 * Mendefinisikan tipe data untuk variabel yang dibutuhkan oleh fungsi mutasi.
 * Dalam kasus ini, hanya ID perusahaan yang diperlukan.
 */
interface DeleteCompanyVariables {
    id: string;
}

/**
 * Custom hook untuk menghapus perusahaan.
 * Menggunakan @tanstack/react-query's useMutation untuk menangani operasi DELETE.
 *
 * @returns Hasil dari useMutation, termasuk fungsi `mutate` dan status seperti `isPending`.
 */
export const useDeleteCompany = () => {
    return useMutation({
        /**
         * mutationFn adalah fungsi yang akan dieksekusi saat `mutate` dipanggil.
         * @param variables - Objek yang berisi ID perusahaan yang akan dihapus.
         */
        mutationFn: async ({ id }: DeleteCompanyVariables) => {
            // Memastikan ID ada sebelum mencoba menghapus.
            if (!id) {
                throw new Error("Company ID is required for deletion.");
            }

            try {
                // Kirim request DELETE ke endpoint spesifik perusahaan.
                const response = await privateApi.delete(`/v4/company/${id}`);
                return response.data;
            } catch (error) {
                console.error(`Failed to delete company with ID ${id}:`, error);
                throw error;
            }
        },

        /**
         * onSuccess dipanggil setelah mutasi berhasil.
         * @param responseData - Data yang dikembalikan oleh API.
         */
        onSuccess: (responseData) => {
            // Validasi data yang diterima dari API untuk memastikan formatnya benar.
            // Untuk DELETE, kita mengharapkan respons tanpa data entitas.
            const validationResult = DeleteCompanyResponseSchema.safeParse(responseData);
            if (!validationResult.success) {
                console.error("API Response Validation Failed on Success:", validationResult.error.errors);
                throw new Error("Invalid data format received from the server after deletion.");
            }
        },

        // Penanganan error spesifik, seperti notifikasi toast, paling baik
        // dilakukan di komponen yang memanggil hook ini, karena komponen
        // memiliki konteks UI yang lebih lengkap.
    });
};